import classNames from 'classnames/bind';
import React, { ButtonHTMLAttributes, forwardRef, Ref } from 'react';

import styles from './Button.module.scss';

const cx = classNames.bind(styles);

export enum ButtonMode {
  PLAIN = 'plain',
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export enum ButtonSize {
  X_SMALL = 'x-small',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  X_LARGE = 'x-large',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  mode?: ButtonMode;
  size?: ButtonSize;
  dataTestId?: string;
  ariaLabel?: string;
  loading?: boolean;
  fluid?: boolean;
  hasEqualSidesPadding?: boolean;
}

const Button = (
  {
    disabled,
    loading,
    ariaLabel,
    children,
    className,
    dataTestId,
    mode = ButtonMode.PLAIN,
    size = mode === ButtonMode.PLAIN ? undefined : ButtonSize.MEDIUM,
    fluid = false,
    onClick,
    hasEqualSidesPadding,
    ...rest
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>
): React.ReactElement => {
  const classNames = cx(
    'body',
    `${mode}`,
    `${size || ''}`,
    { loading, disabled, fluid, 'has-equal-sides-padding': hasEqualSidesPadding },
    className
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (loading || !onClick) {
      return;
    }
    onClick(e);
  };

  return (
    <button
      aria-label={ariaLabel}
      disabled={disabled}
      aria-disabled={disabled}
      className={classNames}
      data-testid={dataTestId}
      onClick={handleClick}
      ref={ref}
      type="button"
      {...rest}
    >
      {children}
    </button>
  );
};

export default forwardRef(Button);
